import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconListDetails, IconPlus, IconEye } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'warning' },
    confirmed: { label: 'Confirmada', color: 'info' },
    in_progress: { label: 'Em Produção', color: 'primary' },
    finished: { label: 'Concluída', color: 'success' },
    cancelled: { label: 'Cancelada', color: 'default' }
};

const ProductionOrders = () => {
    const history = useHistory();
    const [appId, setAppId] = useState(null);
    const [orders, setOrders] = useState([]);
    const [boms, setBoms] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [status, setStatus] = useState({ type: '', message: '' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ bom: '', warehouse: '', quantity_planned: '' });
    const [saving, setSaving] = useState(false);
    const mountedRef = useRef(true);

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                if (mountedRef.current) setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadOrders();
        loadBoms();
        loadWarehouses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, statusFilter]);

    const loadOrders = async () => {
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            const res = await api.get(`v1/apps/${appId}/production_orders/`, { params });
            if (mountedRef.current) setOrders(res.data.production_orders || []);
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: 'Erro ao carregar ordens de produção' });
        }
    };

    const loadBoms = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/boms/`);
            if (mountedRef.current) setBoms(res.data.boms || []);
        } catch (error) {
            console.error(error);
        }
    };

    const loadWarehouses = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/inventory/warehouses/`);
            if (mountedRef.current) setWarehouses(res.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenCreate = () => {
        setForm({ bom: '', warehouse: '', quantity_planned: '' });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.bom) {
            setStatus({ type: 'error', message: 'Selecione a ficha técnica' });
            return;
        }
        if (!form.quantity_planned || Number(form.quantity_planned) <= 0) {
            setStatus({ type: 'error', message: 'Informe uma quantidade maior que zero' });
            return;
        }
        setSaving(true);
        try {
            const res = await api.post(`v1/apps/${appId}/production_orders/`, form);
            setDialogOpen(false);
            history.push(`/dashboard/production/orders/${res.data.id}`);
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar ordem' });
        } finally {
            if (mountedRef.current) setSaving(false);
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconListDetails}
                gradient="blue"
                title="Ordens de Produção"
                subtitle="Produção a partir de fichas técnicas"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenCreate}>
                        Nova Ordem
                    </Button>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                    select
                    label="Status"
                    size="small"
                    sx={{ minWidth: 180 }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <MenuItem value="">Todos</MenuItem>
                    {Object.entries(STATUS_LABEL).map(([value, info]) => (
                        <MenuItem key={value} value={value}>
                            {info.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>

            <GlassCard>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ficha Técnica</TableCell>
                                <TableCell>Produto Final</TableCell>
                                <TableCell>Depósito</TableCell>
                                <TableCell>Qtd. planejada</TableCell>
                                <TableCell>Qtd. produzida</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((order) => {
                                const statusInfo = STATUS_LABEL[order.status] || { label: order.status, color: 'default' };
                                return (
                                    <TableRow key={order.id} hover>
                                        <TableCell>{order.bom_name}</TableCell>
                                        <TableCell>{order.produto_variacao_name}</TableCell>
                                        <TableCell>{order.warehouse_name}</TableCell>
                                        <TableCell>{order.quantity_planned}</TableCell>
                                        <TableCell>{order.quantity_produced ?? '—'}</TableCell>
                                        <TableCell>
                                            <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => history.push(`/dashboard/production/orders/${order.id}`)}>
                                                <IconEye size={16} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {orders.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        Nenhuma ordem de produção cadastrada ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Nova Ordem de Produção</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label="Ficha técnica"
                            fullWidth
                            required
                            value={form.bom}
                            onChange={(e) => setForm({ ...form, bom: e.target.value })}
                        >
                            {boms.map((bom) => (
                                <MenuItem key={bom.id} value={bom.id}>
                                    {bom.name} ({bom.produto_variacao_name})
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Depósito (opcional)"
                            fullWidth
                            value={form.warehouse}
                            onChange={(e) => setForm({ ...form, warehouse: e.target.value })}
                        >
                            <MenuItem value="">Padrão</MenuItem>
                            {warehouses.map((warehouse) => (
                                <MenuItem key={warehouse.id} value={warehouse.id}>
                                    {warehouse.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Quantidade planejada"
                            type="number"
                            fullWidth
                            required
                            value={form.quantity_planned}
                            onChange={(e) => setForm({ ...form, quantity_planned: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Criando...' : 'Criar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProductionOrders;
